import { useLanguage } from '@/components/LanguageContext';
import LandingShellV3 from '@/components/landing/v3/LandingShellV3';
import CtaFinalOscuro from '@/components/landing/v3/CtaFinalOscuro';
import FaqAcordeon from '@/components/landing/v3/FaqAcordeon';
import PricingTableV3 from '@/components/landing/v3/PricingTableV3';
import { pickLandingV3Content } from '@/pages/landingV3.content';

/**
 * PreciosV3 — `/pricing` y sus cuatro traducciones.
 *
 * ── POR QUÉ EXISTE ──────────────────────────────────────────────────────────
 *
 * Porque hasta aquí NO había página de precios en ningún idioma. `/pricing` era
 * de la V2 y al retirarla se redirigió a `/#precios`, el ancla de la home. Para
 * el español eso conservaba la URL con historial, pero para portugués, inglés,
 * alemán y francés significaba que sencillamente no existía una URL de precios:
 * `/pt/precos` y `/en/pricing` devolvían la cáscara vacía del SPA.
 *
 * «precios», «preços» y «pricing» están entre las consultas de más intención
 * comercial que hay. No tener dónde aterrizarlas es dejar de competir por ellas.
 *
 * ── LO QUE SÍ ESTÁ EN EL HTML ESTÁTICO, Y LO QUE NO ─────────────────────────
 *
 * Los IMPORTES no. Vienen de `/billing/active-pricing` y en `renderToString`
 * las consultas de red no se resuelven, así que la tabla sale vacía en el HTML
 * que ve el crawler y la rellena el cliente al montar. Está documentado en
 * `entry-server.jsx` y es la contrapartida de no hornear precios en el código.
 *
 * Por eso la página necesita OTRA sustancia, y de ahí el FAQ: el titular, el
 * subtítulo, los nombres de plan, las once features y seis preguntas con sus
 * respuestas son ~700-900 palabras indexables por idioma. Sin el FAQ serían
 * 250 y ni un número — una página que no compite por nada.
 *
 * El FAQ además le da a esta página su `FAQPage` de schema.org, que lo emite
 * `FaqAcordeon` junto a las preguntas que describe.
 *
 * ── EL TITULAR ──────────────────────────────────────────────────────────────
 *
 * Reutiliza `precios.titulo` y `precios.subtitulo`, que es el texto que la
 * tabla pinta como `<h2>` en la home. Aquí son el `<h1>` de la página y la
 * tabla se monta con `conEncabezado={false}` para no repetirlos.
 */
export default function PreciosV3() {
  const { language } = useLanguage();
  const raiz = pickLandingV3Content(language);
  const c = raiz.precios;

  return (
    <LandingShellV3>
      <section className="bg-white px-6 pt-10 pb-2">
        <div className="max-w-[760px] mx-auto text-center">
          <h1 className="text-[34px] sm:text-[42px] leading-[1.1] tracking-[-.03em] font-bold text-v3-ink">
            {c.titulo}
          </h1>
          <p className="text-[17px] lg:text-[18px] leading-[1.6] text-v3-body mt-5">
            {c.subtitulo}
          </p>
        </div>
      </section>

      <PricingTableV3 conEncabezado={false} />

      <FaqAcordeon titulo={c.faqTitulo} preguntas={c.faq} />

      {/* El cierre es el mismo de la home: el copy no cambia por estar en otra
          página. Lo que SÍ cambia son los ids de CTA, y tienen que cambiar: si
          se compartieran, no habría forma de saber si un alta vino de la home o
          de la página de precios, que es justo la pregunta que esta página
          existe para responder. */}
      <CtaFinalOscuro
        tituloA={raiz.home.cierre.tituloA}
        acento={raiz.home.cierre.acento}
        cta={raiz.home.cierre.cta}
        microcopy={raiz.home.cierre.microcopy}
        enterpriseA={raiz.home.cierre.enterpriseA}
        enterpriseCta={raiz.home.cierre.enterpriseCta}
        ctaTrial="precios.cierre.trial"
        ctaVentas="precios.cierre.ventas"
      />
    </LandingShellV3>
  );
}
